import { getObject } from '@/lib/ai-session/presentation/workflow-message-blocks';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import type { ScreenplayWorkflowSnapshot } from '@/types/ai-session';

export function getBreakdownPayload(
  session: CanonicalAISession | null
): Record<string, unknown> | null {
  if (session?.sessionType !== 1) return null;
  return getObject(session.payload.breakdown);
}

function getAlignmentPayload(session: CanonicalAISession | null): Record<string, unknown> | null {
  if (session?.sessionType !== 1) return null;
  return getObject(session.payload.alignment);
}

export function getBreakdownStrategyPayload(
  session: CanonicalAISession | null
): Record<string, unknown> | null {
  const breakdown = getBreakdownPayload(session);
  return getObject(breakdown?.breakdownStrategy);
}

export function hasGeneratedAlignmentSuggestions(
  session: CanonicalAISession | null,
  draftText: string
): boolean {
  if (draftText.trim()) return true;
  const alignment = getAlignmentPayload(session);
  const compiledSuggestions = alignment?.compiledSuggestions;
  if (typeof compiledSuggestions === 'string' && compiledSuggestions.trim()) return true;
  return Array.isArray(alignment?.versions) && alignment.versions.length > 0;
}

export function getReadingSkillRecommendation(
  session: CanonicalAISession | null
): Record<string, unknown> | null {
  return getObject(getBreakdownStrategyPayload(session)?.scriptTypeRecommendation);
}

/** UI 和断流恢复共用资格判断，当前失败/撤销结果不得被旧断点覆盖。 */
export function getPromptableReadingSkillRecommendation(
  session: CanonicalAISession | null,
  activeCheckpoint?: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (session?.sessionType !== 1) return null;
  const recommendation = getReadingSkillRecommendation(session);
  const detection = getObject(getBreakdownStrategyPayload(session)?.scriptTypeDetection);
  if (
    session.payload.alignment.skillSnapshot.contentType === 'general' ||
    recommendation?.shouldPromptRecommendation === false ||
    isFailedTypeDetection(detection)
  )
    return null;

  const checkpoint = activeCheckpoint ?? session.payload.alignmentCheckpoint;
  const checkpointRecommendation =
    checkpoint?.waitingAction === 'choose_reading_skill'
      ? getObject(checkpoint.recommendation)
      : null;
  if (
    activeCheckpoint &&
    isReliableTypeRecommendation(checkpointRecommendation) &&
    getObject(checkpointRecommendation?.recommendedSkill)
  ) {
    return checkpointRecommendation;
  }
  if (recommendation) {
    return isReliableTypeRecommendation(recommendation) &&
      getObject(recommendation.recommendedSystemSkill)
      ? recommendation
      : null;
  }
  return isReliableTypeRecommendation(checkpointRecommendation) &&
    getObject(checkpointRecommendation?.recommendedSkill)
    ? checkpointRecommendation
    : null;
}

export function hasPromptableReadingSkillRecommendation(
  session: CanonicalAISession | null
): boolean {
  return getPromptableReadingSkillRecommendation(session) !== null;
}

function isFailedTypeDetection(detection: Record<string, unknown> | null): boolean {
  return Boolean(
    detection &&
    (detection.needsClassifierReview === true ||
      detection.classifierFailureCode ||
      detection.detectedScriptType === 'general' ||
      (typeof detection.typeConfidence === 'number' &&
        (!Number.isFinite(detection.typeConfidence) ||
          detection.typeConfidence < 0.65 ||
          detection.typeConfidence > 1)))
  );
}

function isReliableTypeRecommendation(recommendation: Record<string, unknown> | null): boolean {
  if (
    !recommendation ||
    recommendation.shouldPromptRecommendation === false ||
    isFailedTypeDetection(recommendation)
  )
    return false;
  // 服务端 HIGH_CONFIDENCE_THRESHOLD 为 0.65；缺失或失败结果不能借旧断点补成推荐。
  return (
    typeof recommendation.detectedScriptType === 'string' &&
    Boolean(recommendation.detectedScriptType.trim()) &&
    typeof recommendation.typeConfidence === 'number' &&
    Number.isFinite(recommendation.typeConfidence) &&
    recommendation.typeConfidence >= 0.65 &&
    recommendation.typeConfidence <= 1
  );
}

export function getAlignmentSceneMap(
  session: CanonicalAISession | null
): Array<Record<string, unknown>> | undefined {
  if (session?.sessionType !== 1) return undefined;
  const sceneMap = session.payload.alignment.alignmentPackage.sceneMap;
  if (!Array.isArray(sceneMap)) return undefined;
  const rows = sceneMap.map(getObject);
  return rows.every((row): row is Record<string, unknown> => row !== null) ? rows : undefined;
}

export function getReadingStructured(
  session: CanonicalAISession | null,
  workflowSnapshot: ScreenplayWorkflowSnapshot | null
): Record<string, unknown> | null {
  const snapshotReading = getObject(workflowSnapshot?.readingStructured);
  // 精简快照以空对象省略通读正文，不能覆盖会话中已保存的分析结果。
  if (snapshotReading && Object.keys(snapshotReading).length > 0) return snapshotReading;
  return getObject(getBreakdownPayload(session)?.readingStructured) ?? snapshotReading;
}

export function mergeAlignmentSummaryPayload(
  session: CanonicalAISession | null,
  alignmentPackage?: Record<string, unknown>,
  readingStructured?: Record<string, unknown>,
  breakdownStrategy?: Record<string, unknown>
): CanonicalAISession | null {
  if (
    !session ||
    session.sessionType !== 1 ||
    (!alignmentPackage && !readingStructured && !breakdownStrategy)
  ) {
    return session;
  }
  const payload = session.payload;
  const alignment = { ...payload.alignment };
  const breakdown = { ...payload.breakdown };
  if (alignmentPackage) {
    alignment.alignmentPackage = alignmentPackage;
  }
  if (readingStructured) {
    breakdown.readingStructured = readingStructured;
  }
  if (breakdownStrategy) {
    breakdown.breakdownStrategy = breakdownStrategy;
  }
  return {
    ...session,
    payload: {
      ...payload,
      alignment,
      breakdown,
    },
  };
}
